import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
  tryInitPostHog,
  capturePageview,
  isMarketingRoute,
  watchForConsent,
} from '../lib/posthog'

// PostHogTracker
// --------------
// Lives inside <BrowserRouter> and reacts to every route change.
//
// On every route change, this component:
//   1. Checks whether the current route is on the marketing whitelist.
//      If not, does nothing -- PostHog never sees the navigation.
//   2. Calls tryInitPostHog() which is a no-op if PostHog is already
//      initialised or if consent hasn't been granted yet.
//   3. If PostHog is now live, captures the pageview.
//
// It also subscribes once to CookieYes's consent-changed event so that
// if the user accepts analytics AFTER the initial page load, PostHog
// will init the next time they navigate to a marketing route.
//
// Renders nothing.

export default function PostHogTracker() {
  const location = useLocation()

  // Install the consent listener once on mount. The listener handles
  // the case where the user accepts analytics cookies after the page
  // has already loaded.
  useEffect(() => {
    watchForConsent()
  }, [])

  // On every route change, decide whether to init/track.
  useEffect(() => {
    if (!isMarketingRoute(location.pathname)) {
      // Authenticated app surface or client portal -- skip entirely.
      // We don't even attempt init here, so PostHog stays cold for
      // the duration of any in-app session.
      return
    }
    const ready = tryInitPostHog()
    if (ready) {
      capturePageview(location.pathname)
    }
    // If !ready, the user hasn't consented yet. We leave the listener
    // installed (above) so consent acceptance retroactively kicks off
    // tracking from that point on.
  }, [location.pathname])

  return null
}
